import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fetchAuditLog, type AccessAuditEntry } from "@/lib/accessControl";

function describe(e: AccessAuditEntry) {
  const role = e.role_key ?? "a role";
  const who = e.person?.full_name ?? "a deleted account";
  const where = e.scope_label ? ` for ${e.scope_label}` : "";
  const perm = typeof e.details?.permission === "string" ? e.details.permission : "";
  switch (e.action) {
    case "assign": return `Gave ${who} ${role}${where}`;
    case "revoke": return `Removed ${role}${where} from ${who}`;
    case "change": return `Changed ${role}${where} for ${who}`;
    case "role_created": return `Created role ${role}`;
    case "role_updated": return `Updated role ${role}`;
    case "role_deleted": return `Deleted role ${role}`;
    case "permission_added": return `Added ${perm} to ${role}`;
    case "permission_removed": return `Removed ${perm} from ${role}`;
    default: return `${e.action} ${role}`;
  }
}

export function AccessLogTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "access", "audit"],
    queryFn: () => fetchAuditLog(200),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Access log</CardTitle>
        <CardDescription>The last 200 role and permission changes, from any screen or tool.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : error ? (
          <p className="text-sm text-destructive">Could not load the log: {(error as Error).message}</p>
        ) : (data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No changes yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">When</TableHead>
                  <TableHead>Change</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="whitespace-nowrap text-sm">{format(new Date(e.created_at), "MMM d, yyyy h:mm a")}</TableCell>
                    <TableCell className="text-sm">{describe(e)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{e.actor?.full_name ?? "System"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
